import React, { useState, useEffect, useMemo } from "react";
import { Modal, Form, Row, Col, Spinner } from "react-bootstrap";
import { TrendingDown, Star, CreditCard, Info } from "lucide-react";
import { updatePricingAndRegenerate } from "../services/Annonce_service";
import { getPointsSettings } from "../services/Payment_service";

interface EditAnnonceModalProps {
  show: boolean;
  onClose: () => void;
  annonce: any;
  onSave: (updatedAnnonce: any) => void;
}

const EditAnnonceModal: React.FC<EditAnnonceModalProps> = ({
  show,
  onClose,
  annonce,
  onSave,
}) => {
  const [commissionPercentage, setCommissionPercentage] = useState<number>(0);
  const [usedPoints, setUsedPoints] = useState<number>(0);
  const [creditNoteAmount, setCreditNoteAmount] = useState<number>(0);
  const [legalMoTTC, setLegalMoTTC] = useState<number>(Number(annonce?.legalMoTTC ?? 0));
  
  const [pointsSettings, setPointsSettings] = useState<{ point_value: number; multiplier: number }>({ point_value: 0, multiplier: 1 });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchPointsSettings = async () => {
      try {
        const settings = await getPointsSettings();
        setPointsSettings(settings);
      } catch (err) {
        console.error("Erreur lors de la récupération des points:", err);
      }
    };
    fetchPointsSettings();
  }, []);

  useEffect(() => {
    if (annonce) {
      setCommissionPercentage(Number(annonce.commission_percentage ?? 0));
      setUsedPoints(Number(annonce.usedPoints ?? 0));
      setCreditNoteAmount(Number(annonce.creditNoteAmount ?? 0));
      setLegalMoTTC(Number(annonce.legalMoTTC ?? 0));
    }
  }, [annonce]);

 const { commissionValue, pointsValue, finalPrice, totalDiscount } = useMemo(() => {
  const commissionValue = commissionPercentage > 0
    ? (commissionPercentage / 100) * legalMoTTC
    : 0;

  const pointsValue = usedPoints > 0 ? usedPoints * pointsSettings.point_value : 0;
  const totalDiscount = commissionValue + pointsValue + creditNoteAmount;
  const finalPrice = Math.max(legalMoTTC - totalDiscount, 0);

  return { commissionValue, pointsValue, finalPrice, totalDiscount };
}, [commissionPercentage, usedPoints, creditNoteAmount, legalMoTTC, pointsSettings]);

  const handleSubmit = async () => {
    setLoading(true);
    const pricingData = {
      commission_percentage: commissionPercentage,
      usedPoints,
      creditNoteAmount,
    };

    try {
      const response = await updatePricingAndRegenerate(annonce.id, pricingData, false);
      if (response.data.success) {
        onSave(response.data.data.currentPricing);
        setLoading(false);
        onClose();
      } else {
        console.error("Erreur:", response.data.message);
        setLoading(false);
      }
    } catch (error) {
      console.error("Erreur lors de l'enregistrement :", error);
      setLoading(false);
    }
  };


  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <style>{`
        .modern-modal-header {
          background:#4BB0C0;
          border: none;
          padding: 1.5rem;
        }
        
        .modern-modal-title {

          color: white;
          font-weight: 600;
          font-size: 1.25rem;
          margin: 0;
        }
        
        .modern-close-btn {
          color: white;
          opacity: 0.9;
          font-size: 1.5rem;
        }
        
        .modern-close-btn:hover {
          opacity: 1;
        }
        
        .modern-input-group {
          margin-bottom: 1.5rem;
        }
        
        .modern-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
          color: #2d3748;
          margin-bottom: 0.5rem;
          font-size: 0.95rem;
        }
        
        .modern-label svg {
          width: 18px;
          height: 18px;
        }
        
        .modern-input {
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          padding: 0.75rem;
          font-size: 1rem;
          transition: all 0.2s;
        }
        
        .modern-input:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          outline: none;
        }
        
        .summary-card {
          background: linear-gradient(135deg, #f6f8fb 0%, #ffffff 100%);
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 1.5rem;
          margin-top: 1.5rem;
        }
        
        .summary-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1.1rem;
          font-weight: 600;
          color: #2d3748;
          margin-bottom: 1rem;
        }
        
        .summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .summary-row:last-of-type {
          border-bottom: none;
        }
        
        .summary-label {
          color: #4a5568;
          font-size: 0.95rem;
        }
        
        .summary-value {
          font-weight: 600;
          color: #2d3748;
        }
        
        .summary-value.negative {
          color: #e53e3e;
        }
        
        .final-price-section {
          background: linear-gradient(135deg, #4BB0C0 0%, #9ae3efff 100%);
          color: white;
          padding: 1.25rem;
          border-radius: 8px;
          margin-top: 1rem;
          text-align: center;
        }
        
        .final-price-label {
          font-size: 0.9rem;
          opacity: 0.9;
          margin-bottom: 0.25rem;
        }
        
        .final-price-value {
          font-size: 2rem;
          font-weight: 700;
        }
        
        .modern-footer {
          border-top: 1px solid #e2e8f0;
          padding: 1.25rem 1.5rem;
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
        }
        
        .btn-modern-cancel {
          background: white;
          border: 2px solid #e2e8f0;
          color: #4a5568;
          padding: 0.625rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          transition: all 0.2s;
        }
        
        .btn-modern-cancel:hover {
          background: #f7fafc;
          border-color: #cbd5e0;
        }
        
        .btn-modern-save {
          background: #F4A261;
          border: none;
          color: white;
          padding: 0.625rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          transition: all 0.2s;
        }
        
        .btn-modern-save:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }
        
        .modal-body {
          padding: 1.5rem;
        }
      `}</style>
      
      <Modal.Header closeButton className="modern-modal-header">
        <Modal.Title className="modern-modal-title">
          Modifier les remises de l'annonce
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ paddingBottom: "5rem" }}>
        <Form>
          <Row>
            <Col md={4}>
              <div className="modern-input-group">
                <label className="modern-label"><TrendingDown /> Commission (%)</label>
                <Form.Control
                  type="number"
                  min={0}
                  max={100}
                  value={commissionPercentage}
                  onChange={(e) => setCommissionPercentage(Number(e.target.value))}
                  className="modern-input"
                />
              </div>
            </Col>
            <Col md={4}>
              <div className="modern-input-group">
                <label className="modern-label"><Star /> Points utilisés</label>
                <Form.Control
                  type="number"
                  min={0}
                  value={usedPoints}
                  onChange={(e) => setUsedPoints(Number(e.target.value))}
                  className="modern-input"
                />
              </div>
            </Col>
            <Col md={4}>
              <div className="modern-input-group">
                <label className="modern-label"><CreditCard /> Avoir (€)</label>
                <Form.Control
                  type="number"
                  min={0}
                  value={creditNoteAmount}
                  onChange={(e) => setCreditNoteAmount(Number(e.target.value))}
                  className="modern-input"
                />
              </div>
            </Col>
          </Row>
        </Form>

        <div className="summary-card">
          <div className="summary-title"><Info size={20} /> Récapitulatif des prix</div>
          <div className="summary-row">
            <span className="summary-label">Prix initial</span>
            <span className="summary-value">{legalMoTTC} €</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Commission ({commissionPercentage}%)</span>
            <span className="summary-value negative">- {commissionValue} €</span>
          </div>
          {pointsSettings.point_value ? (
            <div className="summary-row">
              <span>Points utilisés ({usedPoints} × {pointsSettings.point_value}€)</span>
              <span className="summary-value negative">- {pointsValue} €</span>
            </div>
          ) : (
            <div className="summary-row">
              <span>Chargement des paramètres de points...</span>
              <span className="summary-value">...</span>
            </div>
          )}
          <div className="summary-row">
            <span className="summary-label">Avoir</span>
            <span className="summary-value negative">- {creditNoteAmount} €</span>
          </div>

          <div className="summary-row total-discount-row" style={{ 
                  borderTop: '2px solid black', 
                  borderBottom: '2px solid black', 
                  fontWeight: '600', 
                  margin: '0.5rem 0'
                }}>
              <span className="summary-label">Total des remises</span>
              <span className="summary-value negative">- {totalDiscount} €</span>
            </div>

          <div className="final-price-section">
            <div className="final-price-label">Prix estimé (TTC) </div>
            <div className="final-price-value">{finalPrice} €</div>
          </div>
        </div>
      </Modal.Body>

      <div className="modern-footer" style={{ position: "sticky", bottom: 0, background: "white", zIndex: 10 }}>
        <button className="btn-modern-cancel" onClick={onClose} disabled={loading}>
          Annuler
        </button>
        <button className="btn-modern-save" onClick={handleSubmit} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : "Enregistrer"}
        </button>
      </div>
    </Modal>
  );
};

export default EditAnnonceModal;